function isVisibleAvailabilityControl(element) {
  let currentElement = element;

  while (currentElement) {
    if (currentElement.hidden || currentElement.getAttribute('aria-hidden') === 'true') return false;

    const styles = window.getComputedStyle(currentElement);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    currentElement = currentElement.parentElement;
  }

  return true;
}

if (!customElements.get('product-availability')) {
  customElements.define(
    'product-availability',
    class ProductAvailability extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleRestockRocketLoaded = this.handleRestockRocketLoaded.bind(this);
        this.handleStoqProductChanged = this.handleStoqProductChanged.bind(this);
        this.updateState = this.updateState.bind(this);
        this.updateCountdown = this.updateCountdown.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.buttonsContainer = this.productInfo?.querySelector('.product-form__buttons');
        this.countdown = this.querySelector('[data-dispatch-countdown]');
        this.dispatchLabel = this.querySelector('[data-dispatch-label]');
        this.similarDialog = this.querySelector('dialog');

        this.addEventListener('click', this.handleClick);
        window.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('stoq:loaded', this.handleRestockRocketLoaded);
        document.addEventListener('stoq:product-changed', this.handleStoqProductChanged);

        if (this.buttonsContainer) {
          this.buttonsObserver = new MutationObserver(this.updateState);
          this.buttonsObserver.observe(this.buttonsContainer, {
            attributes: true,
            attributeFilter: ['class', 'disabled', 'aria-disabled'],
          });
        }

        this.contentObserver = new MutationObserver(this.updateState);
        this.contentObserver.observe(this, { childList: true });

        this.updateState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('stoq:loaded', this.handleRestockRocketLoaded);
        document.removeEventListener('stoq:product-changed', this.handleStoqProductChanged);
        this.buttonsObserver?.disconnect();
        this.contentObserver?.disconnect();
        if (this.stoqDateRefreshTimer) window.clearTimeout(this.stoqDateRefreshTimer);
        this.stopCountdown();
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-similar-products-open]');
        const closeButton = event.target.closest('[data-similar-products-close]');

        if (openButton) {
          event.preventDefault();
          this.openSimilarProducts(openButton);
        } else if (closeButton || event.target === this.similarDialog) {
          event.preventDefault();
          this.closeSimilarProducts();
        }
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.similarDialog?.open) {
          this.closeSimilarProducts();
        }
      }

      openSimilarProducts(trigger) {
        if (!this.similarDialog) return;

        this.lastTrigger = trigger;

        try {
          if (typeof this.similarDialog.showModal === 'function') {
            this.similarDialog.showModal();
          } else {
            this.similarDialog.setAttribute('open', '');
          }
        } catch (error) {
          console.error('Unable to open the similar products dialog.', error);
          return;
        }

        this.similarDialog.querySelector('[data-similar-products-close]')?.focus();
      }

      closeSimilarProducts() {
        if (!this.similarDialog) return;

        if (typeof this.similarDialog.close === 'function' && this.similarDialog.open) {
          this.similarDialog.close();
        } else {
          this.similarDialog.removeAttribute('open');
        }

        this.lastTrigger?.focus();
      }

      updateState() {
        this.countdown = this.querySelector('[data-dispatch-countdown]');
        this.dispatchLabel = this.querySelector('[data-dispatch-label]');
        this.similarDialog = this.querySelector('dialog');

        const submitButton = this.productInfo?.querySelector('.product-form__submit');
        const buttonLabel = String(
          submitButton?.dataset.preorderFallbackOriginalLabel || submitButton?.textContent || ''
        ).trim();
        const variantId = this.productInfo?.querySelector('.product-variant-id')?.value;
        const getSellingPlan = window._RestockRocket?.getSellingPlan;
        let hasStoqPreorderPlan = false;

        if (typeof getSellingPlan === 'function' && variantId) {
          const stoqVariantId = Number(variantId);
          if (Number.isSafeInteger(stoqVariantId)) {
            try {
              hasStoqPreorderPlan = Boolean(getSellingPlan(stoqVariantId));
            } catch (error) {
              console.error('Unable to read the STOQ preorder selling plan.', error);
            }
          }
        }

        const hasPreorderClass = this.buttonsContainer?.classList.contains('product-form__buttons--preorder');
        const hasPreorderDescription = Boolean(
          this.productInfo?.querySelector(
            '.restock-rocket-preorder-description:not([data-theme-preorder-description-fallback])'
          )
        );
        const hasNotifyButton = [...(this.productInfo?.querySelectorAll('button, a, [role="button"]') || [])]
          .some(control => isVisibleAvailabilityControl(control)
            && (control.dataset.themeNotifyButton === 'true'
              || /notify me when available/i.test(String(control.textContent || '').trim())));
        const hasSoldOutLabel = Boolean(submitButton?.disabled)
          && /sold out|out of stock|unavailable/i.test(buttonLabel);
        const renderedState = this.querySelector('[data-availability-panel]')?.dataset.availabilityPanel;
        const hasClientPreorderState = hasStoqPreorderPlan
          || hasPreorderDescription
          || (hasPreorderClass && !hasSoldOutLabel);
        const hasClientSoldOutState = !hasClientPreorderState && (
          hasSoldOutLabel || (hasNotifyButton && Boolean(submitButton?.disabled))
        );
        const state = hasClientPreorderState
          ? 'preorder'
          : hasClientSoldOutState
            ? 'sold-out'
            : renderedState || (submitButton?.disabled ? 'sold-out' : 'in-stock');
        const isPreorder = state === 'preorder';
        const isSoldOut = state === 'sold-out';

        if (isPreorder) {
          const preorderTemplate = this.querySelector('[data-preorder-panel-template]');
          if (preorderTemplate && !this.querySelector('[data-availability-panel="preorder"]')) {
            const preorderPanel = preorderTemplate.content.cloneNode(true);
            preorderPanel.querySelector('[data-availability-panel="preorder"]')?.setAttribute('data-client-preorder-panel', '');
            this.append(preorderPanel);
          }
          this.refreshPreorderDates();
        }
        if (isPreorder || isSoldOut) this.enhanceNotifyButton();

        this.querySelectorAll('[data-availability-panel]').forEach((panel) => {
          panel.toggleAttribute('hidden', panel.dataset.availabilityPanel !== state);
        });

        if (state === 'in-stock') {
          this.startCountdown();
        } else {
          this.stopCountdown();
        }
      }

      handleRestockRocketLoaded() {
        this.updateState();
      }

      handleStoqProductChanged(event) {
        const variantId = event.detail?.variantId;
        const selectedVariantId = this.productInfo?.querySelector('.product-variant-id')?.value;
        if (variantId && selectedVariantId && String(variantId) !== String(selectedVariantId)) return;

        if (this.stoqDateRefreshTimer) window.clearTimeout(this.stoqDateRefreshTimer);
        this.stoqDateRefreshTimer = window.setTimeout(() => {
          this.stoqDateRefreshTimer = undefined;
          this.refreshPreorderDates(variantId);
        }, 0);
      }

      refreshPreorderDates(variantId) {
        if (!this.updatePreorderDatesFromHq()) {
          this.updatePreorderDatesFromSellingPlan(variantId);
        }
      }

      updatePreorderDatesFromHq() {
        const match = String(this.dataset.hqPreorderShipAfter || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return false;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        const shipAfterDate = new Date(Date.UTC(year, month - 1, day, 12));
        if (shipAfterDate.getUTCFullYear() !== year || shipAfterDate.getUTCMonth() !== month - 1 || shipAfterDate.getUTCDate() !== day) return false;

        const shipAfter = this.querySelector('[data-preorder-ship-after]');
        const estimatedDelivery = this.querySelector('[data-preorder-estimated-delivery]');
        if (shipAfter) shipAfter.textContent = this.formatPreorderDate(shipAfterDate);
        if (estimatedDelivery) estimatedDelivery.textContent = this.formatPreorderDate(this.addBusinessDays(shipAfterDate, 3));
        return true;
      }

      updatePreorderDatesFromSellingPlan(variantId) {
        const selectedVariantId = this.productInfo?.querySelector('.product-variant-id')?.value;
        variantId = variantId || selectedVariantId;
        const getSellingPlan = window._RestockRocket?.getSellingPlan;
        if (!variantId || typeof getSellingPlan !== 'function') return false;

        const stoqVariantId = Number(variantId);
        if (!Number.isSafeInteger(stoqVariantId)) return false;

        let sellingPlan;
        try {
          sellingPlan = getSellingPlan(stoqVariantId);
        } catch (error) {
          console.error('Unable to read the STOQ preorder selling plan.', error);
          return false;
        }

        const shipAfterDate = this.parseStoqOfferDate(sellingPlan?.name);
        if (!shipAfterDate) return false;

        const shipAfter = this.querySelector('[data-preorder-ship-after]');
        const estimatedDelivery = this.querySelector('[data-preorder-estimated-delivery]');

        if (shipAfter) shipAfter.textContent = this.formatPreorderDate(shipAfterDate);
        if (estimatedDelivery) {
          estimatedDelivery.textContent = this.formatPreorderDate(this.addBusinessDays(shipAfterDate, 3));
        }

        return true;
      }

      parseStoqOfferDate(offerName) {
        const match = String(offerName || '').match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{4})\b/);
        if (!match) return null;

        const first = Number(match[1]);
        const second = Number(match[2]);
        const year = Number(match[3]);
        const day = first;
        const month = second;
        const date = new Date(Date.UTC(year, month - 1, day, 12));

        if (
          date.getUTCFullYear() !== year ||
          date.getUTCMonth() !== month - 1 ||
          date.getUTCDate() !== day
        ) {
          return null;
        }

        return date;
      }

      addBusinessDays(date, days) {
        const result = new Date(date.getTime());
        let remainingDays = days;

        while (remainingDays > 0) {
          result.setUTCDate(result.getUTCDate() + 1);
          const day = result.getUTCDay();
          if (day !== 0 && day !== 6) remainingDays -= 1;
        }

        return result;
      }

      formatPreorderDate(date) {
        const locale = String(this.dataset.language || document.documentElement.lang || 'en-GB').replace(/_/g, '-');
        const options = { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' };
        let formatter;

        try {
          formatter = new Intl.DateTimeFormat(locale, options);
        } catch (error) {
          console.error('Unable to format the preorder date with the storefront language.', error);
          formatter = new Intl.DateTimeFormat('en-GB', options);
        }

        // English keeps the existing ordinal style; other languages use their native date order.
        if (formatter.resolvedOptions().locale.split('-')[0] !== 'en') return formatter.format(date);

        const parts = formatter.formatToParts(date);
        const values = parts.reduce((result, part) => {
          if (part.type !== 'literal') result[part.type] = part.value;
          return result;
        }, {});

        return `${values.weekday} ${values.day}${this.getDaySuffix(Number(values.day))} ${values.month}`;
      }

      getDaySuffix(day) {
        if (day >= 11 && day <= 13) return 'th';

        return ({ 1: 'st', 2: 'nd', 3: 'rd' })[day % 10] || 'th';
      }

      enhanceNotifyButton() {
        const notifyButton = [...(this.productInfo?.querySelectorAll('button, a, [role="button"]') || [])].find(
          (control) => control.dataset.themeNotifyButton === 'true'
            || /notify me when available/i.test(String(control.textContent || '').trim())
        );
        if (!notifyButton || notifyButton.querySelector('[data-theme-notify-icon]')) return;

        const icon = document.createElement('span');
        icon.dataset.themeNotifyIcon = 'true';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M6 17h12l-1.2-2V10a4.8 4.8 0 0 0-9.6 0v5L6 17ZM10 20h4"/></svg>';
        notifyButton.prepend(icon);
      }

      startCountdown() {
        if (this.countdownTimer) return;

        this.updateCountdown();
        this.countdownTimer = window.setInterval(this.updateCountdown, 1000);
      }

      stopCountdown() {
        if (!this.countdownTimer) return;

        window.clearInterval(this.countdownTimer);
        this.countdownTimer = undefined;
      }

      updateCountdown() {
        if (!this.countdown || !this.dispatchLabel) return;

        const now = new Date();
        const londonNow = this.getLondonParts(now);
        const sameDayDispatch = londonNow.hour < 12;
        const target = this.getLondonTarget(now, sameDayDispatch ? 12 : 0, sameDayDispatch ? 0 : 1);
        const secondsRemaining = Math.max(0, Math.ceil((target.getTime() - now.getTime()) / 1000));
        const hours = Math.floor(secondsRemaining / 3600);
        const minutes = Math.floor((secondsRemaining % 3600) / 60);
        const hoursLabel = this.dataset.hoursLabel || 'hours';
        const minutesLabel = this.dataset.minutesLabel || 'minutes';

        this.countdown.textContent = hours > 0
          ? `${hours} ${hoursLabel} ${minutes} ${minutesLabel}`
          : `${minutes} ${minutesLabel}`;
        this.dispatchLabel.textContent = sameDayDispatch
          ? this.dataset.sameDayLabel || 'same day dispatch'
          : this.dataset.nextDayLabel || 'next day dispatch';
      }

      getLondonParts(date) {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(date);

        return parts.reduce((values, part) => {
          if (part.type !== 'literal') values[part.type] = Number(part.value);
          return values;
        }, {});
      }

      getLondonOffset(date) {
        const parts = this.getLondonParts(date);
        const londonAsUtc = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        );

        return londonAsUtc - date.getTime();
      }

      getLondonTarget(date, hour, dayOffset) {
        const parts = this.getLondonParts(date);
        const targetAsUtc = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day + dayOffset,
          hour,
          0,
          0
        );
        let target = new Date(targetAsUtc - this.getLondonOffset(date));

        target = new Date(targetAsUtc - this.getLondonOffset(target));
        return target;
      }
    }
  );
}
